// client/src/components/vendas/VendaForm.js

import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useMutateVendas, useVenda } from '../../hooks/useVendas';
import { useVendaFormCompleteData } from '../../hooks/useVendaFormData';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';
import useAlert from '../../hooks/useAlert';
import { 
    Save, 
    XCircle, 
    Loader2, 
    Search, 
    Plus, 
    Trash2, 
    User, 
    MapPin, 
    ShoppingCart, 
    CreditCard, 
    DollarSign, 
    Package,
    Filter,
    X,
    AlertCircle,
    CheckCircle2,
    Clock,
    Tag,
    Scan,
    Calculator,
    Receipt,
    Smartphone,
    Monitor,
    Calendar,
    Building,
    Gift,
    Box,
    TrendingUp
} from 'lucide-react';

// Itens carregados de uma venda existente (via processVendaData em
// useVendas.js) só têm `modelo` + `produto` (populado, aninhado) — nunca
// `tipo`/`nome` no nível do item, que são campos derivados só existentes
// quando o item vem do catálogo (todosItensCombinados/adicionarItem). Sem
// esse mapeamento, a aba Itens fica com nome/tipo em branco ao editar.
const MODELO_PARA_TIPO = {
    ItemVenda: 'Produto',
    Servico: 'Servico',
    Recurso: 'Recurso',
    Oferta: 'Oferta',
    PacoteCliente: 'Pacote'
};

const mapearItemParaEdicao = (item) => {
    const produtoRef = item.produto && typeof item.produto === 'object' ? item.produto : null;
    return {
        ...item,
        tipo: MODELO_PARA_TIPO[item.modelo] || item.modelo,
        nome: produtoRef?.nome || item.nome || item.descricao || 'Item',
        descricao: produtoRef?.descricao || item.descricao || '',
        preco: item.precoUnitario
    };
};

const initialState = {
    cliente: '',
    profissionalPrincipal: '',
    unidade: '',
    itens: [],
    descontoGeral: 0,
    observacoes: '',
    statusVenda: 'Aberta',
    statusPagamento: 'Pendente',
    metodoPagamento: '',
    dataVenda: new Date().toISOString().split('T')[0],
    tags: [],
    tipoVenda: 'normal'
};

const VendaForm = ({ onCloseForm, onSaveSuccess, current, showHeader = true, modoPDV = false }) => {
    const { createVenda, updateVenda, adicionarPagamento } = useMutateVendas();
    const { setAlert } = useAlert();
    const { currency, locale } = useTenantI18n();
    // Refetch reativo — mantém pagamentos/saldoDevedor atualizados após
    // adicionarPagamento (a mutation invalida esta mesma query key), sem
    // precisar fechar e reabrir o formulário.
    const { data: vendaAtualizada } = useVenda(current?._id, { enabled: !!current?._id });
    
    const {
        clientes,
        profissionais,
        enderecos,
        metodosPagamento,
        servicos,
        produtos,
        recursos,
        ofertas,
        pacotes,
        quickOptions,
        isLoading: loadingFormData,
        getItemByModelo,
        gerarItemVenda
    } = useVendaFormCompleteData();

    const [formData, setFormData] = useState(initialState);
    const [activeTab, setActiveTab] = useState(modoPDV ? 'pdv' : 'dados');
    const [itensSelecionados, setItensSelecionados] = useState([]);
    const [buscaItem, setBuscaItem] = useState('');
    const [categoriaAtiva, setCategoriaAtiva] = useState('Todos');
    const [modoBuscaRapida, setModoBuscaRapida] = useState(true);
    const [inputBuscaRapida, setInputBuscaRapida] = useState('');
    const [metodoPagamentoSelecionado, setMetodoPagamentoSelecionado] = useState('');
    const [metodoNovoPagamento, setMetodoNovoPagamento] = useState('');
    const [valorNovoPagamento, setValorNovoPagamento] = useState('');

    const isEditing = !!current;
    const vendaParaPagamentos = vendaAtualizada || current;
    const loading = createVenda.isPending || updateVenda.isPending;

    useEffect(() => {
        if (modoPDV) {
            setFormData(prev => ({
                ...prev,
                statusVenda: 'Finalizada',
                statusPagamento: 'Pago',
                dataVenda: new Date().toISOString().split('T')[0],
                tipoVenda: 'pdv'
            }));
            
            if (metodosPagamento.length > 0 && !metodoPagamentoSelecionado) {
                setMetodoPagamentoSelecionado(metodosPagamento[0]._id);
                setFormData(prev => ({ ...prev, metodoPagamento: metodosPagamento[0]._id }));
            }
        }

        // Prioriza vendaAtualizada (fetch dedicado via useVenda, populate mais
        // completo) sobre o `current` recebido da linha da lista — mesmo
        // padrão do detalhe de venda no mobile, que nunca edita a partir dos
        // dados resumidos da lista.
        const vendaFonte = vendaAtualizada || current;
        if (isEditing && vendaFonte) {
            setFormData({
                cliente: vendaFonte.cliente?._id || vendaFonte.cliente || '',
                profissionalPrincipal: vendaFonte.profissionalPrincipal?._id || vendaFonte.profissionalPrincipal || '',
                unidade: vendaFonte.unidade?._id || vendaFonte.unidade || '',
                itens: vendaFonte.itens || [],
                descontoGeral: vendaFonte.descontoGeral || 0,
                observacoes: vendaFonte.observacoes || '',
                statusVenda: vendaFonte.statusVenda || 'Aberta',
                statusPagamento: vendaFonte.statusPagamento || 'Pendente',
                metodoPagamento: vendaFonte.metodoPagamento?._id || vendaFonte.metodoPagamento || '',
                dataVenda: (vendaFonte.dataVenda || new Date().toISOString()).split('T')[0],
                tags: vendaFonte.tags || [],
                tipoVenda: vendaFonte.tipoVenda || 'normal'
            });
            setItensSelecionados((vendaFonte.itens || []).map(mapearItemParaEdicao));
        }
    }, [current, vendaAtualizada, isEditing, modoPDV, metodosPagamento]);

    const itensBuscaRapida = useMemo(() => {
        if (!inputBuscaRapida || inputBuscaRapida.length < 2) return [];

        const termo = inputBuscaRapida.toLowerCase();
        const todosItens = [
            ...quickOptions.servicos,
            ...quickOptions.produtos,
            ...quickOptions.recursos,
            ...quickOptions.ofertas,
            ...quickOptions.pacotes
        ];

        return todosItens.filter(item =>
            item.label.toLowerCase().includes(termo) ||
            item.subLabel?.toLowerCase().includes(termo) ||
            item.data?.descricao?.toLowerCase().includes(termo)
        ).slice(0, 8);
    }, [inputBuscaRapida, quickOptions]);

    const nomeCategoria = (categoria, fallback) => {
        if (categoria && typeof categoria === 'object') return categoria.nome || fallback;
        return categoria || fallback;
    };

    const todosItensCombinados = useMemo(() => {
        const itens = [];

        servicos.forEach(servico => itens.push({
            ...servico,
            tipo: 'Servico',
            modelo: 'Servico',
            preco: servico.valorFixo || 0,
            categoria: nomeCategoria(servico.categoria, 'Serviços'),
            nome: servico.nome,
            descricao: servico.descricao,
            duracao: servico.duracaoSessaoEmMinutos
        }));

        produtos.forEach(produto => itens.push({
            ...produto,
            tipo: 'Produto',
            modelo: 'ItemVenda',
            preco: produto.preco || 0,
            categoria: nomeCategoria(produto.categoria, 'Produtos'),
            nome: produto.nome,
            descricao: produto.descricao,
            controlaEstoque: produto.controlaEstoque,
            quantidadeEmEstoque: produto.quantidadeEmEstoque || 0
        }));

        recursos.forEach(recurso => itens.push({
            ...recurso,
            tipo: 'Recurso',
            modelo: 'Recurso',
            preco: recurso.valorPorHora || 0,
            categoria: 'Recursos',
            nome: recurso.nome,
            descricao: recurso.descricao,
            tipoRecurso: recurso.tipo,
            tipoDePrecificacao: recurso.tipoDePrecificacao,
            disponivelPara: recurso.disponivelPara,
            status: recurso.status
        }));

        ofertas.forEach(oferta => itens.push({
            ...oferta,
            tipo: 'Oferta',
            modelo: 'Oferta',
            preco: oferta.valorTotalEstimado || 0,
            categoria: 'Ofertas',
            nome: oferta.nome,
            descricao: oferta.descricao,
            tipoOferta: oferta.tipo,
            itensIncluidos: oferta.itens?.length || 0,
            ativo: oferta.ativo
        }));

        pacotes.forEach(pacote => itens.push({
            ...pacote,
            tipo: 'Pacote',
            modelo: 'PacoteCliente',
            preco: 0,
            categoria: 'Pacotes',
            nome: `🎁 ${pacote.pacoteComprado?.nome || 'Pacote'}`,
            descricao: `Pacote: ${pacote.sessoesRestantes || pacote.sessoesContratadas} sessões`,
            sessoesRestantes: pacote.sessoesRestantes,
            pacoteId: pacote._id,
            valorTotal: pacote.valorTotal
        }));

        return itens;
    }, [servicos, produtos, recursos, ofertas, pacotes]);

    const categorias = useMemo(() => {
        const categoriasSet = new Set(['Todos']);
        todosItensCombinados.forEach(item => {
            if (item.categoria) categoriasSet.add(item.categoria);
        });
        return Array.from(categoriasSet);
    }, [todosItensCombinados]);

    const itensFiltrados = useMemo(() => {
        let filtrados = todosItensCombinados.filter(item => {
            const categoriaMatch = categoriaAtiva === 'Todos' || item.categoria === categoriaAtiva;
            const buscaMatch = buscaItem === '' || 
                             item.nome?.toLowerCase().includes(buscaItem.toLowerCase());
            return categoriaMatch && buscaMatch;
        });

        const ordem = { 
            Pacote: 0, 
            Oferta: 1, 
            Servico: 2, 
            Produto: 3, 
            Recurso: 4 
        };
        filtrados.sort((a, b) => (ordem[a.tipo] || 5) - (ordem[b.tipo] || 5));

        return filtrados.slice(0, 50);
    }, [todosItensCombinados, categoriaAtiva, buscaItem]);

    const { subtotal, total } = useMemo(() => {
        const subtotalCalculado = itensSelecionados.reduce((total, item) => {
            if (item.tipo === 'Pacote') return total;
            const preco = item.precoUnitario || item.preco || 0;
            const quantidade = item.quantidade || 1;
            const desconto = item.desconto || 0;
            return total + (preco * quantidade) - desconto;
        }, 0);
        
        const totalCalculado = subtotalCalculado - (formData.descontoGeral || 0);
        return { 
            subtotal: Math.max(0, subtotalCalculado), 
            total: Math.max(0, totalCalculado) 
        };
    }, [itensSelecionados, formData.descontoGeral]);

    const onChange = useCallback((evento) => {
        const { name, value, type, checked } = evento.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    }, []);

    const adicionarItem = useCallback((item) => {
        if (item.tipo === 'Pacote' && item.sessoesRestantes <= 0) {
            return setAlert('Este pacote não possui sessões disponíveis.', 'warning');
        }

        if (item.tipo === 'Produto' && item.controlaEstoque && item.quantidadeEmEstoque <= 0) {
            return setAlert('Este produto está fora de estoque.', 'warning');
        }

        if (item.tipo === 'Recurso' && item.status !== 'Ativo') {
            return setAlert('Este recurso não está disponível.', 'warning');
        }

        if (item.tipo === 'Oferta' && !item.ativo) {
            return setAlert('Esta oferta não está ativa.', 'warning');
        }

        const itemComQuantidade = {
            ...item,
            quantidade: ['Pacote', 'Oferta'].includes(item.tipo) ? 1 : (item.quantidade || 1),
            precoUnitario: ['Pacote'].includes(item.tipo) ? 0 : (item.precoUnitario || item.preco || 0),
            desconto: item.desconto || 0
        };
        
        setItensSelecionados(prev => [...prev, itemComQuantidade]);
        setBuscaItem('');
        setInputBuscaRapida('');
        setAlert(`${item.tipo} adicionado ao carrinho!`, 'success');
    }, [setAlert]);

    const removerItem = useCallback((index) => {
        setItensSelecionados(prev => prev.filter((_, i) => i !== index));
    }, []);

    const atualizarQuantidadeItem = useCallback((index, novaQuantidade) => {
        if (novaQuantidade < 1) return;
        
        setItensSelecionados(prev => 
            prev.map((item, i) => {
                if (i === index && !['Pacote', 'Oferta'].includes(item.tipo)) {
                    if (item.controlaEstoque && novaQuantidade > item.quantidadeEmEstoque) {
                        setAlert(`Quantidade indisponível. Estoque: ${item.quantidadeEmEstoque}`, 'warning');
                        return item;
                    }
                    return { ...item, quantidade: novaQuantidade };
                }
                return item;
            })
        );
    }, [setAlert]);

    const atualizarPrecoItem = useCallback((index, novoPreco) => {
        setItensSelecionados(prev => 
            prev.map((item, i) => 
                i === index && !['Pacote'].includes(item.tipo) 
                    ? { ...item, precoUnitario: Math.max(0, novoPreco) } 
                    : item
            )
        );
    }, []);

    const aplicarDescontoItem = useCallback((index, desconto) => {
        setItensSelecionados(prev => 
            prev.map((item, i) => 
                i === index && !['Pacote'].includes(item.tipo)
                    ? { ...item, desconto: Math.max(0, desconto) }
                    : item
            )
        );
    }, []);

    const onSubmit = (evento) => {
        evento.preventDefault();
        
        if (!formData.cliente && !modoPDV) {
            return setAlert('Selecione um cliente.', 'warning');
        }
        
        if (itensSelecionados.length === 0) {
            return setAlert('Adicione pelo menos um item à venda.', 'warning');
        }

        const dadosParaEnviar = {
            ...formData,
            itens: itensSelecionados.map(item => ({
                // Itens carregados de uma venda existente trazem `produto` já
                // populado (objeto) — usar o _id dele. Itens recém-adicionados
                // do catálogo não têm `produto` aninhado; `item._id` ali É o
                // id do próprio catálogo (Serviço/ItemVenda/Recurso/Oferta).
                produto: (item.produto && typeof item.produto === 'object' ? item.produto._id : item.produto) || item.pacoteId || item._id,
                modelo: item.modelo,
                descricao: item.nome,
                quantidade: item.quantidade || 1,
                precoUnitario: item.precoUnitario || item.preco || 0,
                desconto: item.desconto || 0,
                total: ['Pacote'].includes(item.tipo) ? 0 : ((item.precoUnitario || item.preco || 0) * (item.quantidade || 1)) - (item.desconto || 0),
                profissional: formData.profissionalPrincipal,
                ...(item.tipo === 'Pacote' && { pacoteUtilizado: true }),
                ...(item.tipo === 'Recurso' && { 
                    tipoRecurso: item.tipoRecurso,
                    tipoDePrecificacao: item.tipoDePrecificacao
                }),
                ...(item.tipo === 'Oferta' && {
                    tipoOferta: item.tipoOferta,
                    itensIncluidos: item.itensIncluidos
                })
            })),
            subtotal: subtotal,
            total: total,
            tipoVenda: modoPDV ? 'pdv' : 'manual',
            ...(formData.statusPagamento === 'Pago' && metodoPagamentoSelecionado && {
                pagamentos: [{
                    metodo: metodoPagamentoSelecionado,
                    valor: total,
                    status: 'Pago',
                    dataPagamento: new Date().toISOString()
                }]
            })
        };

        const opcoesMutacao = { 
            onSuccess: () => {
                onSaveSuccess();
                setAlert(
                    isEditing ? 'Venda atualizada com sucesso!' : 'Venda criada com sucesso!', 
                    'success'
                );
            },
            onError: (error) => {
                console.error('❌ Erro ao salvar venda:', error);
                const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Erro ao salvar venda. Tente novamente.';
                setAlert(errorMessage, 'error');
            }
        };

        if (isEditing) {
            updateVenda.mutate({ 
                id: current._id, 
                vendaData: dadosParaEnviar 
            }, opcoesMutacao);
        } else {
            createVenda.mutate(dadosParaEnviar, opcoesMutacao);
        }
    };

    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);

    const handleAdicionarPagamento = useCallback(() => {
        if (!metodoNovoPagamento) {
            return setAlert('Selecione o método de pagamento.', 'warning');
        }
        const valor = parseFloat(valorNovoPagamento);
        if (!valor || valor <= 0) {
            return setAlert('Informe um valor válido.', 'warning');
        }
        adicionarPagamento.mutate({
            id: current._id,
            pagamentoData: {
                metodo: metodoNovoPagamento,
                valor,
                status: 'Pago',
                dataPagamento: new Date().toISOString()
            }
        }, {
            onSuccess: () => {
                setMetodoNovoPagamento('');
                setValorNovoPagamento('');
            }
        });
    }, [metodoNovoPagamento, valorNovoPagamento, adicionarPagamento, current, setAlert]);

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            Servico: 'bg-blue-100 text-blue-800',
            Produto: 'bg-green-100 text-green-800',
            Recurso: 'bg-orange-100 text-orange-800',
            Oferta: 'bg-purple-100 text-purple-800',
            Pacote: 'bg-indigo-100 text-indigo-800'
        };
        return classes[tipo] || 'bg-gray-100 text-gray-800';
    };

    const getTipoIcon = (tipo) => {
        const icons = {
            Servico: Clock,
            Produto: Package,
            Recurso: Building,
            Oferta: Gift,
            Pacote: Box
        };
        return icons[tipo] || Package;
    };

    if (modoPDV) {
        return (
            <div className="pdv-advanced-container">
                <div className="pdv-advanced-header">
                    <div className="flex items-center gap-3">
                        <Monitor size={28} className="text-blue-600" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">PDV Avançado</h2>
                            <p className="text-gray-600">Sistema completo de ponto de venda</p>
                        </div>
                    </div>
                    <button
                        onClick={onCloseForm}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Fechar PDV"
                    >
                        <XCircle size={24} />
                    </button>
                </div>

                <div className="pdv-advanced-content">
                    <div className="pdv-products-column">
                        <div className="pdv-search-section">
                            <div className="search-mode-toggle">
                                <button
                                    type="button"
                                    className={`search-mode-btn ${modoBuscaRapida ? 'active' : ''}`}
                                    onClick={() => setModoBuscaRapida(true)}
                                >
                                    <Scan size={18} />
                                    Busca Rápida
                                </button>
                                <button
                                    type="button"
                                    className={`search-mode-btn ${!modoBuscaRapida ? 'active' : ''}`}
                                    onClick={() => setModoBuscaRapida(false)}
                                >
                                    <Filter size={18} />
                                    Categorias
                                </button>
                            </div>

                            {modoBuscaRapida ? (
                                <div className="quick-search">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="text"
                                            placeholder="Digite o nome do produto/serviço..."
                                            value={inputBuscaRapida}
                                            onChange={(e) => setInputBuscaRapida(e.target.value)}
                                            className="quick-search-input"
                                        />
                                        {inputBuscaRapida && (
                                            <button
                                                type="button"
                                                onClick={() => setInputBuscaRapida('')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>

                                    {inputBuscaRapida && (
                                        <div className="quick-search-results">
                                            {itensBuscaRapida.length === 0 ? (
                                                <div className="no-results">
                                                    <Search size={32} className="text-gray-300" />
                                                    <p>Nenhum item encontrado</p>
                                                </div>
                                            ) : (
                                                <div className="quick-results-grid">
                                                    {itensBuscaRapida.map((item, index) => (
                                                        <button
                                                            key={`${item.modelo}-${item.value}-${index}`}
                                                            onClick={() => adicionarItem(item.data)}
                                                            className="quick-result-item"
                                                        >
                                                            <div className="quick-result-header">
                                                                <span className="quick-result-name">{item.label}</span>
                                                                <span className={`quick-result-type type-${item.modelo?.toLowerCase()}`}>
                                                                    {item.modelo}
                                                                </span>
                                                            </div>
                                                            <div className="quick-result-price">
                                                                {item.modelo === 'PacoteCliente' ? (
                                                                    <span className="text-green-600 text-sm">JÁ PAGO</span>
                                                                ) : (
                                                                    formatCurrency(item.data.preco || item.data.valorFixo || item.data.valorPorHora || 0)
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="categories-mode">
                                    <div className="categories-filter">
                                        {categorias.map(categoria => (
                                            <button
                                                key={categoria}
                                                type="button"
                                                className={`category-filter-btn ${categoriaAtiva === categoria ? 'active' : ''}`}
                                                onClick={() => setCategoriaAtiva(categoria)}
                                            >
                                                {categoria}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="products-grid">
                                        {itensFiltrados.map((item, index) => (
                                            <button
                                                key={`${item._id}-${index}`}
                                                onClick={() => adicionarItem(item)}
                                                className="product-card"
                                                disabled={
                                                    (item.tipo === 'Pacote' && item.sessoesRestantes <= 0) ||
                                                    (item.tipo === 'Produto' && item.controlaEstoque && item.quantidadeEmEstoque <= 0) ||
                                                    (item.tipo === 'Recurso' && item.status !== 'Ativo') ||
                                                    (item.tipo === 'Oferta' && !item.ativo)
                                                }
                                            >
                                                <div className="product-header">
                                                    <span className="product-name">{item.nome}</span>
                                                    <span className={`product-type type-${item.tipo?.toLowerCase()}`}>
                                                        {item.tipo}
                                                    </span>
                                                </div>
                                                
                                                <p className="product-description">{item.descricao}</p>

                                                {item.tipo === 'Pacote' && (
                                                    <div className="pacote-info">
                                                        <div className="sessoes-info">
                                                            <Package size={14} />
                                                            <span>{item.sessoesRestantes} sessões</span>
                                                        </div>
                                                        <div className="text-green-600 text-xs font-medium">
                                                            ✅ Já pago
                                                        </div>
                                                    </div>
                                                )}

                                                {item.tipo === 'Recurso' && (
                                                    <div className="recurso-info">
                                                        <div className="recurso-details">
                                                            <Building size={14} />
                                                            <span>{item.tipoRecurso} • {item.tipoDePrecificacao}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {item.tipo === 'Oferta' && (
                                                    <div className="oferta-info">
                                                        <div className="oferta-details">
                                                            <Gift size={14} />
                                                            <span>{item.tipoOferta} • {item.itensIncluidos} itens</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {item.tipo !== 'Pacote' && (
                                                    <div className="product-price">
                                                        {formatCurrency(item.preco || 0)}
                                                    </div>
                                                )}

                                                {item.tipo === 'Produto' && item.controlaEstoque && (
                                                    <div className={`stock-info ${item.quantidadeEmEstoque > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                                        Estoque: {item.quantidadeEmEstoque}
                                                    </div>
                                                )}

                                                {item.tipo === 'Recurso' && (
                                                    <div className={`stock-info ${item.status === 'Ativo' ? 'in-stock' : 'out-of-stock'}`}>
                                                        {item.status === 'Ativo' ? 'Disponível' : 'Indisponível'}
                                                    </div>
                                                )}

                                                {item.tipo === 'Oferta' && (
                                                    <div className={`stock-info ${item.ativo ? 'in-stock' : 'out-of-stock'}`}>
                                                        {item.ativo ? 'Ativa' : 'Inativa'}
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pdv-checkout-column">
                        <div className="checkout-section">
                            <h3 className="section-title">
                                <User size={18} />
                                Cliente
                            </h3>
                            <select
                                value={formData.cliente}
                                onChange={onChange}
                                name="cliente"
                                className="checkout-select"
                            >
                                <option value="">Cliente não identificado</option>
                                {clientes.map(cliente => (
                                    <option key={cliente._id} value={cliente._id}>
                                        {cliente.nome}
                                        {cliente.telefone && ` - ${cliente.telefone}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="checkout-section">
                            <h3 className="section-title">
                                <ShoppingCart size={18} />
                                Carrinho ({itensSelecionados.length})
                            </h3>
                            
                            <div className="cart-items">
                                {itensSelecionados.length === 0 ? (
                                    <div className="empty-cart">
                                        <ShoppingCart size={48} className="text-gray-300" />
                                        <p>Carrinho vazio</p>
                                        <p className="text-sm text-gray-500">Adicione itens para continuar</p>
                                    </div>
                                ) : (
                                    itensSelecionados.map((item, index) => (
                                        <div key={index} className="cart-item">
                                            <div className="cart-item-info">
                                                <div className="cart-item-header">
                                                    <span className="cart-item-name">
                                                        {item.tipo === 'Pacote' && '🎁 '}
                                                        {item.tipo === 'Oferta' && '🎯 '}
                                                        {item.tipo === 'Recurso' && '🏢 '}
                                                        {item.nome}
                                                    </span>
                                                    <button
                                                        onClick={() => removerItem(index)}
                                                        className="remove-item-btn"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                
                                                {!['Pacote'].includes(item.tipo) && (
                                                    <div className="cart-item-controls">
                                                        <div className="quantity-control">
                                                            <label>Qtd:</label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={item.quantidade || 1}
                                                                onChange={(e) => atualizarQuantidadeItem(index, parseInt(e.target.value))}
                                                                className="quantity-input"
                                                            />
                                                        </div>
                                                        <div className="price-control">
                                                            <label>Preço:</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={item.precoUnitario || item.preco || 0}
                                                                onChange={(e) => atualizarPrecoItem(index, parseFloat(e.target.value))}
                                                                className="price-input"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="cart-item-total">
                                                {item.tipo === 'Pacote' ? (
                                                    <span className="text-green-600 font-medium">JÁ PAGO</span>
                                                ) : (
                                                    formatCurrency(
                                                        ((item.precoUnitario || item.preco || 0) * (item.quantidade || 1)) - (item.desconto || 0)
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="checkout-section">
                            <h3 className="section-title">
                                <Calculator size={18} />
                                Resumo
                            </h3>
                            
                            <div className="summary">
                                <div className="summary-row">
                                    <span>Subtotal:</span>
                                    <span>{formatCurrency(subtotal)}</span>
                                </div>
                                
                                <div className="summary-row">
                                    <span>Desconto Geral:</span>
                                    <div className="discount-input-group">
                                        <span>R$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max={subtotal}
                                            value={formData.descontoGeral}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                descontoGeral: parseFloat(e.target.value) || 0
                                            }))}
                                            className="discount-input"
                                        />
                                    </div>
                                </div>
                                
                                <div className="summary-row total">
                                    <span>Total:</span>
                                    <span className="total-amount">{formatCurrency(total)}</span>
                                </div>
                            </div>

                            <div className="payment-methods">
                                <h4 className="payment-title">
                                    <CreditCard size={16} />
                                    Método de Pagamento
                                </h4>
                                <div className="payment-options">
                                    {metodosPagamento.map(metodo => (
                                        <button
                                            key={metodo._id}
                                            type="button"
                                            className={`payment-option ${metodoPagamentoSelecionado === metodo._id ? 'selected' : ''}`}
                                            onClick={() => {
                                                setMetodoPagamentoSelecionado(metodo._id);
                                                setFormData(prev => ({ ...prev, metodoPagamento: metodo._id }));
                                            }}
                                        >
                                            {metodo.nome}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="checkout-actions">
                                <button
                                    type="button"
                                    onClick={() => setItensSelecionados([])}
                                    className="clear-cart-btn"
                                >
                                    <Trash2 size={18} />
                                    Limpar
                                </button>
                                <button
                                    onClick={onSubmit}
                                    disabled={loading || itensSelecionados.length === 0 || !metodoPagamentoSelecionado}
                                    className="checkout-btn"
                                >
                                    {loading ? (
                                        <Loader2 size={18} className="animate-spin" />
                                    ) : (
                                        <Receipt size={18} />
                                    )}
                                    Finalizar Venda
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-panel">
            <div className="form-header">
                <h3 className="form-title">
                    <TrendingUp className="icon-title" />
                    {isEditing ? `Editar Venda` : 'Nova Venda'}
                    {loadingFormData && <span className="loading-text"> (Carregando dados...)</span>}
                </h3>
                <button 
                    type="button" 
                    onClick={onCloseForm} 
                    title="Fechar Formulário" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            <form onSubmit={onSubmit} noValidate>
                <div className="form-tabs-nav">
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'dados' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('dados')}
                    >
                        <User size={16}/> Dados da Venda
                    </button>
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'itens' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('itens')}
                    >
                        <ShoppingCart size={16}/> Itens ({itensSelecionados.length})
                    </button>
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'pagamento' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('pagamento')}
                    >
                        <CreditCard size={16}/> Pagamento
                    </button>
                </div>

                <div className="form-tabs-content">
                    {activeTab === 'dados' && (
                        <div className="form-grid-container">
                            <div className="form-section">
                                <h4 className="form-section-title">Informações Principais</h4>

                                {isEditing && (current?.veioDePedido || current?.veioDeAgendamento) && (
                                    <div className="form-group">
                                        <span className="form-hint">
                                            Origem: {current.origemFormatada || (current.veioDePedido ? 'Pedido' : 'Agendamento')}
                                            {current.pedidoOrigem?.numero ? ` #${current.pedidoOrigem.numero}` : ''}
                                        </span>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="cliente">Cliente *</label>
                                    <select 
                                        name="cliente" 
                                        id="cliente" 
                                        value={formData.cliente} 
                                        onChange={onChange} 
                                        required 
                                        className="form-input"
                                        disabled={loadingFormData}
                                    >
                                        <option value="">Selecione um cliente</option>
                                        {clientes.map(cliente => (
                                            <option key={cliente._id} value={cliente._id}>
                                                {cliente.nome}
                                                {cliente.telefone ? ` - ${cliente.telefone}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="profissionalPrincipal">Profissional Responsável</label>
                                    <select 
                                        name="profissionalPrincipal" 
                                        id="profissionalPrincipal" 
                                        value={formData.profissionalPrincipal} 
                                        onChange={onChange} 
                                        className="form-input"
                                        disabled={loadingFormData}
                                    >
                                        <option value="">Selecione um profissional</option>
                                        {profissionais.map(profissional => (
                                            <option key={profissional._id} value={profissional._id}>
                                                {profissional.name}
                                                {profissional.email ? ` - ${profissional.email}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="unidade">Endereço/Unidade *</label>
                                    <select
                                        name="unidade"
                                        id="unidade"
                                        value={formData.unidade}
                                        onChange={onChange}
                                        required
                                        className="form-input"
                                        disabled={loadingFormData}
                                    >
                                        <option value="">Selecione uma unidade</option>
                                        {enderecos.map(unidade => (
                                            <option key={unidade._id} value={unidade._id}>
                                                {unidade.nome}{unidade.localidade ? ` - ${unidade.localidade}` : ''}{unidade.uf ? `, ${unidade.uf}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="dataVenda">Data da Venda</label>
                                    <input 
                                        type="date" 
                                        name="dataVenda" 
                                        id="dataVenda" 
                                        value={formData.dataVenda} 
                                        onChange={onChange} 
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="form-section-title">Resumo Financeiro</h4>
                                
                                <div className="summary-card">
                                    <div className="summary-item-list">
                                        <div className="summary-item">
                                            <span className="item-name">Subtotal:</span>
                                            <span className="item-price">{formatCurrency(subtotal)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="descontoGeral">Desconto Geral (R$)</label>
                                        <input 
                                            type="number" 
                                            name="descontoGeral" 
                                            id="descontoGeral" 
                                            value={formData.descontoGeral} 
                                            onChange={onChange} 
                                            min="0"
                                            step="0.01"
                                            max={subtotal}
                                            className="form-input"
                                        />
                                    </div>
                                    
                                    <div className="summary-totals">
                                        <div className="total-row is-grand-total">
                                            <span>Total:</span>
                                            <span className="text-green-600">{formatCurrency(total)}</span>
                                        </div>
                                    </div>

                                    {pacotes.length > 0 && (
                                        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                                            <div className="flex items-center gap-2 text-blue-800">
                                                <Package size={16} />
                                                <span className="font-medium">{pacotes.length} pacote(s) disponível(is)</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="observacoes">Observações</label>
                                    <textarea 
                                        name="observacoes" 
                                        id="observacoes" 
                                        value={formData.observacoes} 
                                        onChange={onChange} 
                                        rows="4"
                                        className="form-input"
                                        placeholder="Observações sobre a venda..."
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="tags">Tags (opcional)</label>
                                    <input 
                                        type="text" 
                                        name="tags" 
                                        id="tags" 
                                        value={formData.tags.join(', ')} 
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                                        }))}
                                        className="form-input"
                                        placeholder="tag1, tag2, tag3"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Separe as tags por vírgula
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'itens' && (
                        <div className="form-section">
                            <h4 className="form-section-title">
                                Adicionar Itens à Venda
                                <span className="text-sm font-normal text-gray-500 ml-2">
                                    ({todosItensCombinados.length} itens disponíveis)
                                </span>
                            </h4>

                            {loadingFormData ? (
                                <div className="loading-indicator">
                                    <Loader2 size={20} className="animate-spin" />
                                    <span>Carregando produtos e serviços...</span>
                                </div>
                            ) : (
                                <>
                                    <div className="search-filters mb-6">
                                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                                            <div className="flex-1 w-full lg:max-w-md">
                                                <div className="search-box">
                                                    <Search size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar produtos ou serviços por nome..."
                                                        value={buscaItem}
                                                        onChange={(evento) => setBuscaItem(evento.target.value)}
                                                        className="search-input"
                                                    />
                                                    {buscaItem && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setBuscaItem('')}
                                                            className="clear-search-button"
                                                            title="Limpar busca"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-wrap gap-2 items-center">
                                                <div className="category-filters">
                                                    {categorias.map(categoria => (
                                                        <button
                                                            key={categoria}
                                                            type="button"
                                                            className={`category-filter ${categoriaAtiva === categoria ? 'active' : ''}`}
                                                            onClick={() => setCategoriaAtiva(categoria)}
                                                        >
                                                            {categoria}
                                                            {categoria === 'Pacotes' && pacotes.length > 0 && (
                                                                <span className="badge">{pacotes.length}</span>
                                                            )}
                                                            {categoria === 'Recursos' && recursos.length > 0 && (
                                                                <span className="badge">{recursos.length}</span>
                                                            )}
                                                            {categoria === 'Ofertas' && ofertas.length > 0 && (
                                                                <span className="badge">{ofertas.length}</span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                                
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setBuscaItem('');
                                                        setCategoriaAtiva('Todos');
                                                    }}
                                                    className="action-button action-button-secondary compact"
                                                >
                                                    <X size={14} />
                                                    Limpar Filtros
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-container">
                                        <div className="table-header">
                                            <h2>Itens Disponíveis ({itensFiltrados.length})</h2>
                                        </div>
                                        <div className="table-wrapper">
                                            {itensFiltrados.length === 0 ? (
                                                <div className="table-empty-state">
                                                    <Search size={64} className="text-gray-300 mb-4" />
                                                    <h3 className="empty-title">
                                                        {buscaItem || categoriaAtiva !== 'Todos' 
                                                            ? 'Nenhum item encontrado' 
                                                            : 'Nenhum item disponível'
                                                        }
                                                    </h3>
                                                    <p className="empty-description">
                                                        {buscaItem || categoriaAtiva !== 'Todos'
                                                            ? 'Tente ajustar os filtros ou buscar por outros termos.'
                                                            : 'Cadastre produtos e serviços no sistema para começar.'
                                                        }
                                                    </p>
                                                    {(buscaItem || categoriaAtiva !== 'Todos') && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setBuscaItem('');
                                                                setCategoriaAtiva('Todos');
                                                            }}
                                                            className="action-button action-button-primary mt-4"
                                                        >
                                                            <Filter size={16} />
                                                            Limpar Filtros
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <table className="styled-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-left">Item</th>
                                                            <th className="text-center">Tipo</th>
                                                            <th className="text-center">Categoria</th>
                                                            <th className="text-center">Preço</th>
                                                            <th className="text-center">Disponibilidade</th>
                                                            <th className="text-center">Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {itensFiltrados.map((item, index) => {
                                                            const IconComponent = getTipoIcon(item.tipo);
                                                            return (
                                                                <tr key={`${item._id}-${index}`} className="hover:bg-gray-50">
                                                                    <td className="text-left">
                                                                        <div className="flex flex-col">
                                                                            <span className="font-medium text-gray-900">
                                                                                {item.tipo === 'Pacote' && '🎁 '}
                                                                                {item.tipo === 'Oferta' && '🎯 '}
                                                                                {item.tipo === 'Recurso' && '🏢 '}
                                                                                {item.nome}
                                                                            </span>
                                                                            {item.descricao && (
                                                                                <span className="text-sm text-gray-500 mt-1">
                                                                                    {item.descricao}
                                                                                </span>
                                                                            )}
                                                                            {item.tipo === 'Recurso' && (
                                                                                <div className="flex items-center gap-2 mt-1">
                                                                                    <Building size={14} className="text-orange-500" />
                                                                                    <span className="text-xs text-orange-600">
                                                                                        {item.tipoRecurso} • {item.tipoDePrecificacao}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            {item.tipo === 'Oferta' && (
                                                                                <div className="flex items-center gap-2 mt-1">
                                                                                    <Gift size={14} className="text-purple-500" />
                                                                                    <span className="text-xs text-purple-600">
                                                                                        {item.tipoOferta} • {item.itensIncluidos} itens
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    
                                                                    <td className="text-center">
                                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeClass(item.tipo)}`}>
                                                                            <IconComponent size={12} className="mr-1" />
                                                                            {item.tipo}
                                                                        </span>
                                                                    </td>
                                                                    
                                                                    <td className="text-center">
                                                                        <span className="text-sm text-gray-600">
                                                                            {item.categoria || 'Geral'}
                                                                        </span>
                                                                    </td>
                                                                    
                                                                    <td className="text-center">
                                                                        {item.tipo === 'Pacote' ? (
                                                                            <div className="flex flex-col items-center">
                                                                                <span className="text-green-600 text-sm font-medium">
                                                                                    JÁ PAGO
                                                                                </span>
                                                                                <span className="text-xs text-gray-500">
                                                                                    {formatCurrency(item.valorTotal || 0)}
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="font-semibold text-gray-900">
                                                                                {formatCurrency(item.preco || 0)}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    
                                                                    <td className="text-center">
                                                                        {item.tipo === 'Produto' && item.controlaEstoque ? (
                                                                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                                item.quantidadeEmEstoque > 0 
                                                                                    ? 'bg-green-100 text-green-800'
                                                                                    : 'bg-red-100 text-red-800'
                                                                            }`}>
                                                                                <Tag size={12} className="mr-1" />
                                                                                {item.quantidadeEmEstoque}
                                                                            </div>
                                                                        ) : item.tipo === 'Pacote' ? (
                                                                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                <Clock size={12} className="mr-1" />
                                                                                {item.sessoesRestantes || 0}
                                                                            </div>
                                                                        ) : item.tipo === 'Recurso' ? (
                                                                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                                <CheckCircle2 size={12} className="mr-1" />
                                                                                {item.status === 'Ativo' ? 'Ativo' : 'Inativo'}
                                                                            </div>
                                                                        ) : item.tipo === 'Oferta' ? (
                                                                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                                <CheckCircle2 size={12} className="mr-1" />
                                                                                {item.ativo ? 'Ativa' : 'Inativa'}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-gray-400 text-xs">—</span>
                                                                        )}
                                                                    </td>
                                                                    
                                                                    <td className="text-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => adicionarItem(item)}
                                                                            className={`action-button ${
                                                                                item.tipo === 'Pacote' 
                                                                                    ? 'action-button-success' 
                                                                                    : item.tipo === 'Oferta'
                                                                                    ? 'action-button-warning'
                                                                                    : 'action-button-primary'
                                                                            } compact`}
                                                                            disabled={
                                                                                (item.tipo === 'Pacote' && item.sessoesRestantes <= 0) ||
                                                                                (item.tipo === 'Produto' && item.controlaEstoque && item.quantidadeEmEstoque <= 0) ||
                                                                                (item.tipo === 'Recurso' && item.status !== 'Ativo') ||
                                                                                (item.tipo === 'Oferta' && !item.ativo)
                                                                            }
                                                                            title={
                                                                                (item.tipo === 'Pacote' && item.sessoesRestantes <= 0)
                                                                                    ? 'Pacote sem sessões disponíveis'
                                                                                    : (item.tipo === 'Produto' && item.controlaEstoque && item.quantidadeEmEstoque <= 0)
                                                                                    ? 'Produto fora de estoque'
                                                                                    : (item.tipo === 'Recurso' && item.status !== 'Ativo')
                                                                                    ? 'Recurso não disponível'
                                                                                    : (item.tipo === 'Oferta' && !item.ativo)
                                                                                    ? 'Oferta não ativa'
                                                                                    : item.tipo === 'Pacote'
                                                                                    ? 'Usar pacote'
                                                                                    : item.tipo === 'Oferta'
                                                                                    ? 'Adicionar oferta'
                                                                                    : 'Adicionar item'
                                                                            }
                                                                        >
                                                                            <Plus size={16} />
                                                                            {item.tipo === 'Pacote' ? 'Usar' : 
                                                                             item.tipo === 'Oferta' ? 'Oferta' : 'Add'}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {itensSelecionados.length > 0 && (
                                <div className="selected-items-section mt-8">
                                    <div className="table-container">
                                        <div className="table-header">
                                            <h2>
                                                Itens Selecionados ({itensSelecionados.length})
                                                <span className="text-sm font-normal text-gray-500 ml-2">
                                                    {itensSelecionados.filter(item => !['Pacote', 'Oferta'].includes(item.tipo)).length} item(s) • 
                                                    {itensSelecionados.filter(item => item.tipo === 'Pacote').length} pacote(s) •
                                                    {itensSelecionados.filter(item => item.tipo === 'Oferta').length} oferta(s) •
                                                    {itensSelecionados.filter(item => item.tipo === 'Recurso').length} recurso(s)
                                                </span>
                                            </h2>
                                        </div>
                                        <div className="table-wrapper">
                                            <table className="styled-table">
                                                <thead>
                                                    <tr>
                                                        <th className="text-left">Item</th>
                                                        <th className="text-center">Tipo</th>
                                                        <th className="text-center">Quantidade</th>
                                                        <th className="text-center">Preço Unit.</th>
                                                        <th className="text-center">Desconto</th>
                                                        <th className="text-center">Total</th>
                                                        <th className="text-center">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {itensSelecionados.map((item, index) => {
                                                        const IconComponent = getTipoIcon(item.tipo);
                                                        return (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="text-left">
                                                                    <div className="flex flex-col">
                                                                        <span className="font-medium text-gray-900">
                                                                            {item.tipo === 'Pacote' && '🎁 '}
                                                                            {item.tipo === 'Oferta' && '🎯 '}
                                                                            {item.tipo === 'Recurso' && '🏢 '}
                                                                            {item.nome}
                                                                        </span>
                                                                        {item.descricao && (
                                                                            <span className="text-sm text-gray-500 mt-1">
                                                                                {item.descricao}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                
                                                                <td className="text-center">
                                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeClass(item.tipo)}`}>
                                                                        <IconComponent size={12} className="mr-1" />
                                                                        {item.tipo}
                                                                    </span>
                                                                </td>
                                                                
                                                                <td className="text-center">
                                                                    {!['Pacote'].includes(item.tipo) ? (
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            max={item.controlaEstoque ? item.quantidadeEmEstoque : 999}
                                                                            value={item.quantidade || 1}
                                                                            onChange={(evento) => atualizarQuantidadeItem(index, parseInt(evento.target.value))}
                                                                            className="form-input text-center w-20 mx-auto"
                                                                        />
                                                                    ) : (
                                                                        <span className="text-gray-400">—</span>
                                                                    )}
                                                                </td>
                                                                
                                                                <td className="text-center">
                                                                    {!['Pacote'].includes(item.tipo) ? (
                                                                        <input
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            value={item.precoUnitario || item.preco || 0}
                                                                            onChange={(evento) => atualizarPrecoItem(index, parseFloat(evento.target.value))}
                                                                            className="form-input text-center w-24 mx-auto"
                                                                        />
                                                                    ) : (
                                                                        <span className="text-green-600 font-medium">JÁ PAGO</span>
                                                                    )}
                                                                </td>
                                                                
                                                                <td className="text-center">
                                                                    {!['Pacote'].includes(item.tipo) ? (
                                                                        <input
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            max={(item.precoUnitario || item.preco || 0) * (item.quantidade || 1)}
                                                                            value={item.desconto || 0}
                                                                            onChange={(evento) => aplicarDescontoItem(index, parseFloat(evento.target.value))}
                                                                            className="form-input text-center w-24 mx-auto"
                                                                        />
                                                                    ) : (
                                                                        <span className="text-gray-400">—</span>
                                                                    )}
                                                                </td>
                                                                
                                                                <td className="text-center font-semibold">
                                                                    {item.tipo === 'Pacote' ? (
                                                                        <span className="text-green-600">JÁ PAGO</span>
                                                                    ) : (
                                                                        formatCurrency(
                                                                            ((item.precoUnitario || item.preco || 0) * (item.quantidade || 1)) - (item.desconto || 0)
                                                                        )
                                                                    )}
                                                                </td>
                                                                
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removerItem(index)}
                                                                        className="action-icon-button action-icon-delete"
                                                                        title="Remover item"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="summary-card mt-4">
                                        <h5 className="summary-title">Resumo da Venda</h5>
                                        <div className="summary-item-list">
                                            <div className="summary-item">
                                                <span className="item-name">Subtotal dos Itens:</span>
                                                <span className="item-price">{formatCurrency(subtotal)}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="item-name">Desconto Geral:</span>
                                                <span className="item-price text-red-600">
                                                    - {formatCurrency(formData.descontoGeral || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="summary-totals">
                                            <div className="total-row is-grand-total">
                                                <span>Total a Pagar:</span>
                                                <span className="text-green-600">{formatCurrency(total)}</span>
                                            </div>
                                        </div>
                                        {itensSelecionados.filter(item => item.tipo === 'Pacote').length > 0 && (
                                            <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                                                <div className="flex items-center gap-2 text-green-800">
                                                    <Package size={16} />
                                                    <span className="font-medium">
                                                        {itensSelecionados.filter(item => item.tipo === 'Pacote').length} pacote(s) incluído(s) - Já pagos
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {itensSelecionados.filter(item => item.tipo === 'Oferta').length > 0 && (
                                            <div className="mt-2 p-3 bg-purple-50 rounded border border-purple-200">
                                                <div className="flex items-center gap-2 text-purple-800">
                                                    <Gift size={16} />
                                                    <span className="font-medium">
                                                        {itensSelecionados.filter(item => item.tipo === 'Oferta').length} oferta(s) incluída(s)
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {itensSelecionados.filter(item => item.tipo === 'Recurso').length > 0 && (
                                            <div className="mt-2 p-3 bg-orange-50 rounded border border-orange-200">
                                                <div className="flex items-center gap-2 text-orange-800">
                                                    <Building size={16} />
                                                    <span className="font-medium">
                                                        {itensSelecionados.filter(item => item.tipo === 'Recurso').length} recurso(s) incluído(s)
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'pagamento' && (
                        <div className="form-section">
                            <h4 className="form-section-title">Informações de Pagamento</h4>
                            
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label htmlFor="statusVenda">Status da Venda</label>
                                    <select 
                                        name="statusVenda" 
                                        id="statusVenda" 
                                        value={formData.statusVenda}
                                        onChange={onChange}
                                        className="form-input"
                                    >
                                        <option value="Aberta">Aberta</option>
                                        <option value="Finalizada">Finalizada</option>
                                        <option value="Cancelada">Cancelada</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="statusPagamento">Status do Pagamento</label>
                                    <select 
                                        name="statusPagamento" 
                                        id="statusPagamento" 
                                        value={formData.statusPagamento}
                                        onChange={onChange}
                                        className="form-input"
                                    >
                                        <option value="Pendente">Pendente</option>
                                        <option value="Parcialmente Pago">Parcialmente Pago</option>
                                        <option value="Pago">Pago</option>
                                        <option value="Inadimplente">Inadimplente</option>
                                    </select>
                                </div>

                                {(formData.statusPagamento === 'Pago' || formData.statusPagamento === 'Parcialmente Pago') && (
                                    <div className="form-group">
                                        <label htmlFor="metodoPagamento">
                                            Método de Pagamento *
                                        </label>
                                        <select 
                                            name="metodoPagamento" 
                                            id="metodoPagamento" 
                                            value={formData.metodoPagamento}
                                            onChange={onChange}
                                            required={formData.statusPagamento === 'Pago' || formData.statusPagamento === 'Parcialmente Pago'}
                                            className="form-input"
                                            disabled={loadingFormData}
                                        >
                                            <option value="">Selecione um método de pagamento</option>
                                            {metodosPagamento.map(metodo => (
                                                <option key={metodo._id} value={metodo._id}>
                                                    {metodo.nome} 
                                                    {metodo.tipo && ` (${metodo.tipo})`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="summary-card">
                                <h5 className="summary-title">Resumo do Pagamento</h5>
                                <div className="summary-item-list">
                                    <div className="summary-item">
                                        <span className="item-name">Total de Itens:</span>
                                        <span className="item-price">{formatCurrency(subtotal)}</span>
                                    </div>
                                    
                                    {itensSelecionados.filter(item => item.tipo === 'Pacote').length > 0 && (
                                        <div className="summary-item">
                                            <span className="item-name">Pacotes Incluídos:</span>
                                            <span className="item-price text-green-600">
                                                {itensSelecionados.filter(item => item.tipo === 'Pacote').length} pacote(s)
                                            </span>
                                        </div>
                                    )}
                                    
                                    {itensSelecionados.filter(item => item.tipo === 'Oferta').length > 0 && (
                                        <div className="summary-item">
                                            <span className="item-name">Ofertas Incluídas:</span>
                                            <span className="item-price text-purple-600">
                                                {itensSelecionados.filter(item => item.tipo === 'Oferta').length} oferta(s)
                                            </span>
                                        </div>
                                    )}
                                    
                                    {itensSelecionados.filter(item => item.tipo === 'Recurso').length > 0 && (
                                        <div className="summary-item">
                                            <span className="item-name">Recursos Incluídos:</span>
                                            <span className="item-price text-orange-600">
                                                {itensSelecionados.filter(item => item.tipo === 'Recurso').length} recurso(s)
                                            </span>
                                        </div>
                                    )}
                                    
                                    <div className="summary-item">
                                        <span className="item-name">Desconto Geral:</span>
                                        <span className="item-price text-red-600">
                                            - {formatCurrency(formData.descontoGeral || 0)}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="summary-totals">
                                    <div className="total-row is-grand-total">
                                        <span>Total Final:</span>
                                        <span className="text-green-600">{formatCurrency(total)}</span>
                                    </div>
                                </div>
                            </div>

                            {isEditing && (
                                <div className="form-section mt-6">
                                    <h4 className="form-section-title">Pagamentos</h4>

                                    <div className="summary-card">
                                        <div className="summary-item-list">
                                            <div className="summary-item">
                                                <span className="item-name">Total da Venda:</span>
                                                <span className="item-price">{formatCurrency(vendaParaPagamentos?.total)}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="item-name">Total Pago:</span>
                                                <span className="item-price text-green-600">{formatCurrency(vendaParaPagamentos?.totalPago)}</span>
                                            </div>
                                            {(vendaParaPagamentos?.saldoDevedor || 0) > 0 && (
                                                <div className="summary-item">
                                                    <span className="item-name text-red-600">Saldo Devedor:</span>
                                                    <span className="item-price text-red-600">{formatCurrency(vendaParaPagamentos?.saldoDevedor)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {vendaParaPagamentos?.pagamentos?.length > 0 && (
                                        <div className="table-container mt-4">
                                            <div className="table-wrapper">
                                                <table className="styled-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-left">Método</th>
                                                            <th className="text-left">Data</th>
                                                            <th className="text-right">Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {vendaParaPagamentos.pagamentos.map((pagamento, index) => (
                                                            <tr key={pagamento._id || index}>
                                                                <td className="text-left">
                                                                    {typeof pagamento.metodo === 'object' ? (pagamento.metodo?.nome || 'Pagamento') : 'Pagamento'}
                                                                </td>
                                                                <td className="text-left">
                                                                    {pagamento.dataPagamento
                                                                        ? new Date(pagamento.dataPagamento).toLocaleDateString('pt-BR')
                                                                        : '-'}
                                                                </td>
                                                                <td className="text-right">{formatCurrency(pagamento.valor)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {vendaParaPagamentos?.statusVenda !== 'Cancelada' && (vendaParaPagamentos?.saldoDevedor || 0) > 0 && (
                                        <div className="form-grid-container-cols-2 mt-4">
                                            <div className="form-group">
                                                <label htmlFor="metodoNovoPagamento">Método de Pagamento</label>
                                                <select
                                                    id="metodoNovoPagamento"
                                                    value={metodoNovoPagamento}
                                                    onChange={(e) => setMetodoNovoPagamento(e.target.value)}
                                                    className="form-input"
                                                    disabled={loadingFormData}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {metodosPagamento.map(metodo => (
                                                        <option key={metodo._id} value={metodo._id}>{metodo.nome}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="valorNovoPagamento">Valor (R$)</label>
                                                <input
                                                    type="number"
                                                    id="valorNovoPagamento"
                                                    step="0.01"
                                                    min="0"
                                                    value={valorNovoPagamento}
                                                    onChange={(e) => setValorNovoPagamento(e.target.value)}
                                                    className="form-input"
                                                    placeholder={`Saldo devedor: ${formatCurrency(vendaParaPagamentos.saldoDevedor)}`}
                                                />
                                            </div>
                                            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                                <button
                                                    type="button"
                                                    onClick={handleAdicionarPagamento}
                                                    disabled={adicionarPagamento.isPending}
                                                    className="action-button action-button-primary"
                                                >
                                                    {adicionarPagamento.isPending ? (
                                                        <Loader2 size={16} className="animate-spin" />
                                                    ) : (
                                                        <DollarSign size={16} />
                                                    )}
                                                    Adicionar Pagamento
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button 
                        type="button" 
                        onClick={onCloseForm} 
                        className="action-button action-button-secondary"
                        disabled={loading}
                    >
                        <XCircle size={18} /> 
                        Fechar
                    </button>
                    <button 
                        type="submit" 
                        disabled={loading || loadingFormData || itensSelecionados.length === 0} 
                        className="action-button action-button-primary"
                    >
                        {loading ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <Save size={18} />
                        )} 
                        {isEditing ? 'Salvar Alterações' : 'Criar Venda'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VendaForm;